/**
 * Format a number as currency
 * @param {number} value - The number to format
 * @returns {string} The formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "$0.00";

  // Convert to number if it's a string
  const numAmount =
    typeof amount === "string"
      ? parseFloat(amount.replace(/[$,]/g, ""))
      : amount;

  // Handle NaN
  if (isNaN(numAmount)) return "$0.00";

  // Format the number with commas and 2 decimal places
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

/**
 * Format a value as currency for display in an input field
 * @param {string|number} value - The input value to format
 * @returns {string} The formatted currency string
 */
export const formatCurrencyInput = (value) => {
  // Handle empty or invalid input
  if (value === null || value === undefined || value === "") {
    return "$0.00";
  }

  // Remove any existing formatting
  let numericString = String(value).replace(/[$,]/g, "");

  // Handle special cases
  if (numericString === ".") return "$0.";
  if (numericString === "0.") return "$0.";
  if (numericString.startsWith(".")) numericString = "0" + numericString;

  // Split into integer and decimal parts
  let [integerPart, decimalPart] = numericString.split(".");

  // Handle integer part
  integerPart = integerPart || "0";
  integerPart = integerPart.replace(/^0+/, "") || "0"; // Remove leading zeros but keep single 0

  // Add commas to integer part
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Handle decimal part
  if (decimalPart !== undefined) {
    // If there's a decimal point, show whatever is typed
    return "$" + integerPart + "." + decimalPart;
  } else if (numericString.endsWith(".")) {
    // If the user just typed a decimal point, show it
    return "$" + integerPart + ".";
  }

  // No decimal part
  return "$" + integerPart;
};

/**
 * Process and normalize raw input for currency fields
 * @param {string} input - The raw user input
 * @returns {string} Sanitized numeric string
 */
export const processCurrencyInput = (input) => {
  // Remove all non-numeric characters except decimal point
  let processed = input.replace(/[^\d.]/g, "");

  // Handle multiple decimal points
  const parts = processed.split(".");
  if (parts.length > 2) {
    processed = parts[0] + "." + parts.slice(1).join("");
  }

  // Handle decimal precision
  if (parts.length === 2) {
    processed = parts[0] + "." + parts[1].slice(0, 2);
  }

  return processed;
};

/**
 * Format currency value when input loses focus
 * @param {string|number} value - The value to format
 * @returns {string} Properly formatted currency value
 */
export const formatCurrencyOnBlur = (value) => {
  if (value === null || value === undefined || value === "" || value === ".") {
    return "$0.00";
  }

  // Convert to number and handle invalid input
  const numericValue = parseFloat(String(value).replace(/[$,]/g, ""));
  if (isNaN(numericValue)) return "$0.00";

  // Format with exactly 2 decimal places
  return formatCurrency(numericValue);
};

/**
 * Extract numeric value from formatted currency string
 * @param {string} formattedValue - The formatted currency string
 * @returns {number} The numeric value
 */
export const getCurrencyValue = (formattedValue) => {
  if (!formattedValue) return 0;

  const numericValue = parseFloat(String(formattedValue).replace(/[$,]/g, ""));
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Format a number as a percentage
 * @param {number} value - The number to format
 * @param {number} [decimals=1] - Number of decimal places
 * @returns {string} The formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "0%";
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return formatter.format(value / 100);
};
